import type {
  ParameterAdditionOptions,
  ParameterAdditionResult,
  ProcessProjectParameterScope,
} from '../../../shared/lib/hooks/useParameterAddition';
import type { BoltConfigurationResult } from '../../../shared/services/boltConfigurationService';
import type {
  ConnectorBindingConfiguration,
  MappingConfiguration,
  MenuConfiguration,
  ObjectTypeMapping,
} from '../../../shared/types';
import { runHeadlessQuickFillAutoFillRepair } from '../../quick-fill';
import type { MappingRepairProjectBinding } from './selectedMappingParameterScopeRequirements';
import type { MappingParameterRepairKind } from './mappingProjectReadiness';
import {
  type MappingRepairProgressCallbacks,
  type RepairActions,
  getRepairFailureMessage,
  mapBoltRepairResultToParameterAdditionResult,
  resolvePostVerificationFailureMessage,
  runRepairActions,
  type MappingRepairActionResult,
} from './mappingRepairRun';
import {
  buildQuickFillRepairProgressModel,
  type MappingRepairProgressStepId,
  type ScopedRepairStepProgress,
} from './mappingRepairProgress';
import { toTraceKey, traceMappingFlow, warnMappingFlow } from './mappingTrace';
import type { MappingRepairVerificationResult } from './mappingRepairVerification';
import { runMappingRepairVerification } from './mappingRepairVerificationRun';

interface CreateMappingRepairActionsInput {
  addParameters: (options?: ParameterAdditionOptions) => Promise<ParameterAdditionResult>;
  addAdditionalCategoryParameters: (options?: ParameterAdditionOptions) => Promise<ParameterAdditionResult>;
  addProcessAndProjectParameters: (
    options?: ParameterAdditionOptions,
    scope?: ProcessProjectParameterScope
  ) => Promise<ParameterAdditionResult>;
  processProjectParameterScope: ProcessProjectParameterScope;
  repairProjectBindings: MappingRepairProjectBinding[];
  addMappedParameters: (names: string[], options?: ParameterAdditionOptions) => Promise<ParameterAdditionResult>;
  applyBoltConfigurationRepair: () => Promise<BoltConfigurationResult>;
  projectKey: string | null;
  modelVersion: number | null;
  menuConfig: MenuConfiguration;
  connectorConfig: ConnectorBindingConfiguration;
  objectTypes: ObjectTypeMapping[];
  resolveFamilyRepairKindsAfterClassification?: () => Promise<MappingParameterRepairKind[]>;
  isSameProjectRepair: () => boolean;
  setRepairStepProgress: (value: ScopedRepairStepProgress | null) => void;
}

export function createMappingRepairActions(input: CreateMappingRepairActionsInput): RepairActions {
  const mappingConfiguration = {
    general: { mappingTable: { objectType: input.objectTypes } },
  } as MappingConfiguration;

  return {
    addParameters: input.addParameters,
    addAdditionalCategoryParameters: input.addAdditionalCategoryParameters,
    addProcessAndProjectParameters: (options) =>
      input.addProcessAndProjectParameters(options, input.processProjectParameterScope),
    addMappedParameters: input.addMappedParameters,
    addBoltParameters: async () => mapBoltRepairResultToParameterAdditionResult(
      await input.applyBoltConfigurationRepair()
    ),
    resolveFamilyRepairKindsAfterClassification:
      input.resolveFamilyRepairKindsAfterClassification,
    runQuickFillAutoFillRepair: async (scope) => runHeadlessQuickFillAutoFillRepair({
      projectKey: input.projectKey,
      modelVersion: input.modelVersion,
      menuConfig: input.menuConfig,
      connectorConfig: input.connectorConfig,
      mappingConfiguration,
      repairProjectBindings: input.repairProjectBindings,
      scope,
      onStage: (stage) => {
        if (input.isSameProjectRepair()) {
          input.setRepairStepProgress({
            stepId: scope === 'classification' ? 'quickFillClassification' : 'quickFill',
            model: buildQuickFillRepairProgressModel(stage),
          });
        }
      },
      onSaveProgress: (progress) => {
        if (!input.isSameProjectRepair()) {
          return;
        }
        input.setRepairStepProgress({
          stepId: scope === 'classification' ? 'quickFillClassification' : 'quickFill',
          model: {
            title: 'Saving Quick Fill classifications',
            message: progress.message || 'Revit is saving generated Quick Fill setup values',
            detail: progress.detail,
            status: progress.failed ? 'error' : progress.resultReady ? 'complete' : 'running',
            state: progress.state,
            currentItem: progress.currentItem,
            processed: progress.current,
            total: progress.total,
            percent: typeof progress.percent === 'number' ? progress.percent : null,
            resultReady: progress.resultReady,
            error: progress.error,
            items: progress.items,
            locked: !progress.resultReady && !progress.failed,
          },
        });
      },
    }),
  };
}

export interface MappingRepairExecutionInput {
  repairKinds: MappingParameterRepairKind[];
  actions: RepairActions;
  progressCallbacks: MappingRepairProgressCallbacks;
  projectKey: string | null;
  signature: string;
  familyRepairGroups: string[];
  missingParameterNames?: string[];
  mappedParameterNames?: string[];
  mountedRef: { current: boolean };
  currentSignatureRef: { current: string };
  isCurrentRepair: () => boolean;
  updateRepairStep: (
    stepId: MappingRepairProgressStepId,
    status: 'current' | 'complete' | 'error'
  ) => void;
  retainFailureProgress: (message: string) => void;
  setRepairStepProgress: (value: ScopedRepairStepProgress | null) => void;
  setRepairError: (value: MappingRepairError | null) => void;
  verifyRepairParameters: (
    repairActionResults?: MappingRepairActionResult[]
  ) => Promise<MappingRepairVerificationResult>;
}

export interface MappingRepairError {
  signature: string;
  message: string;
  retainWhenReadinessSatisfied?: boolean;
}

export async function runMappingRepairExecution(input: MappingRepairExecutionInput): Promise<void> {
  const repairRun = await runRepairActions(
    input.repairKinds,
    input.actions,
    { projectKey: input.projectKey, signature: input.signature },
    input.progressCallbacks,
    input.mappedParameterNames ?? []
  );
  const rawFailureMessage = getRepairFailureMessage(repairRun.results);
  const quickFillResult = repairRun.quickFillResult;
  const quickFillFailureMessage = quickFillResult && quickFillResult.failureCount > 0
    ? quickFillResult.savedCount > 0
      ? `Quick Fill auto-fill did not fully complete: ${quickFillResult.error ?? 'Some generated classifications could not be saved.'}`
      : `Quick Fill auto-fill did not complete: ${quickFillResult.error ?? 'Revit did not save all generated classifications.'}`
    : null;
  const reportedFailureMessage = Array.from(new Set(
    [rawFailureMessage, quickFillFailureMessage].filter(
      (message): message is string => Boolean(message)
    )
  )).join(' ');

  if (reportedFailureMessage) {
    warnMappingFlow('mapping readiness repair failed', {
      projectKey: input.projectKey,
      signatureKey: toTraceKey(input.signature),
      stoppedEarly: repairRun.stoppedEarly,
      message: reportedFailureMessage,
    });
  }
  if (!input.isCurrentRepair()) {
    traceMappingFlow('mapping readiness repair stale ignored', {
      projectKey: input.projectKey,
      signatureKey: toTraceKey(input.signature),
      currentSignatureKey: toTraceKey(input.currentSignatureRef.current),
      mounted: input.mountedRef.current,
      signatureMatches: input.currentSignatureRef.current === input.signature,
    });
    return;
  }

  warnMappingFlow('mapping readiness repair result', {
    projectKey: input.projectKey,
    signatureKey: toTraceKey(input.signature),
    repairKinds: input.repairKinds,
    success: !reportedFailureMessage,
    familyRepairGroupCount: input.familyRepairGroups.length,
    missingParameterCount: input.missingParameterNames?.length ?? 0,
    reportedFailureMessage,
    rawFailureMessage,
    quickFillFailureMessage,
    resultCount: repairRun.results.length,
    resultsSummary: repairRun.results.map(({ kind, result }) => ({
      kind,
      success: result.success,
      addedCount: result.addedCount,
      updatedCount: result.updatedCount,
      existingCount: result.existingCount,
      failedCount: result.failedCount,
      conflictingCount: result.conflictingCount,
      requestedCount: result.requestedCount,
      error: result.error ?? null,
      failureMessageCount: result.failureMessages?.length ?? 0,
    })),
    quickFillAttempted: quickFillResult?.attempted ?? false,
    quickFillSuccess: quickFillResult?.success ?? null,
    quickFillSavedCount: quickFillResult?.savedCount ?? 0,
    quickFillError: quickFillResult?.error ?? null,
    failedParameterCount: repairRun.results.reduce(
      (count, { result }) => count + (result.failedParameters?.length ?? 0), 0
    ),
    conflictingParameterCount: repairRun.results.reduce(
      (count, { result }) => count + (result.conflictingParameters?.length ?? 0), 0
    ),
  });

  const verificationFailure = await runMappingRepairVerification({
    projectKey: input.projectKey,
    signature: input.signature,
    verifyRepairParameters: () => input.verifyRepairParameters(repairRun.results),
    updateRepairStep: (status) => input.updateRepairStep('verify', status),
    retainFailureProgress: input.retainFailureProgress,
    setRepairStepProgress: input.setRepairStepProgress,
    setRepairError: input.setRepairError,
  });
  if (verificationFailure !== null) {
    if (reportedFailureMessage) {
      const message = `${verificationFailure} ${reportedFailureMessage}`;
      input.retainFailureProgress(message);
      input.setRepairError({
        signature: input.signature,
        message,
        retainWhenReadinessSatisfied: quickFillFailureMessage !== null,
      });
    }
    return;
  }
  if (!reportedFailureMessage) {
    return;
  }

  const postVerificationFailureMessage = resolvePostVerificationFailureMessage(
    rawFailureMessage,
    quickFillFailureMessage
  );
  if (!postVerificationFailureMessage) {
    warnMappingFlow('mapping readiness repair partial action failures accepted', {
      projectKey: input.projectKey,
      signatureKey: toTraceKey(input.signature),
      message: rawFailureMessage,
    });
    return;
  }
  input.retainFailureProgress(postVerificationFailureMessage);
  input.setRepairError({
    signature: input.signature,
    message: postVerificationFailureMessage,
    retainWhenReadinessSatisfied: true,
  });
}
